import React, { useCallback, useRef, useState, forwardRef } from 'react';
import { WidthProvider, Responsive } from "react-grid-layout";

const ResponsiveGridLayout = WidthProvider(Responsive);

import WidgetLibrary from './WidgetLibraryLazy.jsx';
import SkeletonGrid from '../../skeleton_loaders/common/SkeletonGrid.jsx';
import Header from './HeaderGen2.jsx';
import { useDashboardLayout } from './hooks/useDashboardLayout.js';
import Widget from './gen2_widgets/base/Widget.jsx';
import { Provider, useSelector } from 'react-redux';
import dashboardStore from './redux/dashboardStore.js';
import SettingsSidebar from './settings_sidebar/SettingsSidebar.jsx';
import { useSidebar } from './hooks/useSidebar.js';
import { INITIAL_WIDGET_DIMENSION } from './redux/dashboardSlice.js';
import useRegisterDatasourcesV2 from './datasource_v2/useRegisterDatasourcesV2.js';
import { useElementWidth } from './hooks/useElementWidth.js';

export const arraysHaveSameElements = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((item, index) => item === sorted2[index]);
}

export const GRID_HEIGHT = 'calc(100vh - 56px - 56px - 10px)';

function DashboardGridIndexWrapper(props) {
  return (
    <Provider store={dashboardStore}>
      <DashboardGridIndex {...props} />
    </Provider>
  )
}

const WidgetGridItem = forwardRef(({ key,
  className,
  layoutId,
  isItemVisible,
  widgetId,
  editable,
  isEditing,
  permissions,
  additionalFeatures,
  children,
  ...rest }, ref) => {
  return <div 
    ref={ref}
    key={layoutId} 
    className={className} 
    data-id={layoutId} 
    {...rest}
  >
    {isItemVisible && <Widget
      widgetId={widgetId}
      editable={editable}
      isEditing={isEditing}
      widgetHeight={rest.style.height}
      permissions={permissions}
      additionalFeatures={additionalFeatures}
    />}
    {children}
  </div>
});

function DashboardGridIndex({dashboardId, projectId, editing: initialEditing, mayEdit, colorScheme, asPdf, datasetContainer, returnUrl, showProfilePics,
    widgetSettings, disableRefresh = false, permissions, additionalFeatures
}) {
  const gap = useSelector(state => state.dashboard.theme?.background?.gap);
  const padding = useSelector(state => state.dashboard.theme?.background?.padding);
  const backgroundColor = useSelector(state => state.dashboard.theme?.background?.backgroundColor);
  const widgetIds = useSelector(state => Object.keys(state.dashboard.widgets), arraysHaveSameElements);
  const layoutIds = useSelector(state => Object.keys(state.dashboard.layout), arraysHaveSameElements);
  const cols = useSelector(state => state.dashboard.cols);
  const rowHeight = useSelector(state => state.dashboard.rowHeight);
  const available_widgets = useSelector(state => state.dashboard.available_widgets, (prev, next) => prev.length === next.length);
  const hidden_items = useSelector(state => state.dashboard.hidden_items);
  const editingId = useSelector(state => state.dashboard.editingId);
  const [editing, setEditing] = useState(initialEditing);
  const [libraryOpened, setLibraryOpened] = useState(false);
  const containerRef = useRef();
  const wrapperRef = useRef();
  const {
    onLayoutChange,
    onDrop,
    isItemVisible,
    editable,
    finalLayouts,
    onDrag
  } = useDashboardLayout({projectId, dashboardId, editing, containerRef, colorScheme, datasetContainer, asPdf, showProfilePics, widgetSettings, disableRefresh,
    datasourceV2Enabled: permissions?.datasource_v2, datasourceV2Editable: permissions?.manage_datasource_v2});
  // Lets filter widgets target the dashboard's v2 datasources (editors only)
  useRegisterDatasourcesV2(permissions?.manage_datasource_v2);
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const wrapperWidth = useElementWidth(wrapperRef);
  const dashboardWidth = Math.max(wrapperWidth - sidebarWidth, 0);

  if (!dashboardId) {
    return <SkeletonGrid gridHeight={GRID_HEIGHT} />
  }

  const toggleLibrary = useCallback(() => {
    setLibraryOpened(prev => !prev)
  }, [])

  return (
      <div style={{display: 'flex', flexDirection: 'row', width: '100%'}} ref={wrapperRef}>
        <div id='widget-area' className={`dashboard-grid-layout ${editable ? 'editing' : ''}`} ref={containerRef} style={{width: dashboardWidth, backgroundColor: backgroundColor}}>
          {(mayEdit ?
            <Header
              editing={editing}
              setEditing={setEditing}
              returnUrl={returnUrl}
              toggleLibrary={toggleLibrary}
              /> : null)}
              {(libraryOpened ?
                <WidgetLibrary
                  dashboardId={dashboardId}
                  projectId={projectId}
                  availableWidgets={available_widgets}
                  callbacks={{
                    startDrag: () => {},
                    toggleLibrary: toggleLibrary
                  }}
                  colorScheme={colorScheme}
                />
            : null)}
          <ResponsiveGridLayout
            layouts={finalLayouts}
            droppingItem={{
              ...INITIAL_WIDGET_DIMENSION,
              i: '__dropping-elem__'
            }}
            preventCollision={false}
            breakpoints={{ lg: 525, sm: 0 }}
            cols={{ lg: cols, sm: 16 }}
            className={`layout ${editable ? 'editable' : ''} `}
            rowHeight={rowHeight}
            width={1200}
            onLayoutChange={onLayoutChange}
            isResizable={editable}
            isDraggable={editable}
            resizeHandles={editable ? ['se', 'sw', 'ne', 'nw', 'e', 'w', 'n', 's'] : []}
            margin={[gap ?? 5, gap ?? 5]}
            containerPadding={[padding ?? 0, padding ?? 0]}
            isDroppable={true}
            onDrop={onDrop}
            onDrag={onDrag}
          >
            {widgetIds.map((id) => {
              const layoutComponent = layoutIds.find(val => val === id);
              const layoutId = `widget-${id}`
              const isEditing = editingId == id;
              if (!layoutComponent) return null;

              const hidden = hidden_items.map(String).includes(String(id));

              return (!hidden && <WidgetGridItem
                key={layoutId} 
                className={`widget-grid-item ${isEditing ? 'selected-widget' : ''} ${editingId && !isEditing ? 'widget-blur' : ''} ${hidden ? 'hidden' : ''}`}
                layoutId={layoutId}
                isItemVisible={isItemVisible(id)}
                widgetId={id}
                editable={editable}
                isEditing={isEditing}
                permissions={permissions}
                additionalFeatures={additionalFeatures}
              />);
            })}
          </ResponsiveGridLayout>
        </div>
        <SettingsSidebar editingId={editingId} width={sidebarWidth} setWidth={setSidebarWidth} />
      </div>
  )
}

export default DashboardGridIndexWrapper;